import {
  ChatStartupError,
  type ChatStartupErrorContactAction,
} from "@hopper-ai/chat-widget";
import { useI18nContext } from "@hopper-b2b/i18n";

/** Matches the startup threshold the other tenants use. */
export const HTS_ASSIST_CHAT_START_TIMEOUT_MS = 20_000;

interface HtsAssistChatStartupErrorProps {
  /** Retry callback supplied by the widget's `startupTimeout.renderError`. */
  onRetry: () => void;
}

/**
 * Startup-failure surface for Assist, shown in place of the transcript when the
 * conversation does not start in time. Offers a retry and the travel specialist
 * number so a customer who cannot reach Assist still has a route to support.
 * Shared by the floating widget and the standalone webview page.
 *
 * The contact card reuses the `core-self-serve.contactSupport` copy that the
 * Contact Support modal already shows, so the heading, call label and support
 * hours stay identical in both languages instead of drifting. RBC has no
 * live-chat channel (`chatAvailability: None`), so the phone row is the whole
 * fallback; with no number on the brand the card is omitted and the surface is
 * just the message and Try again.
 */
export const HtsAssistChatStartupError: React.FC<
  HtsAssistChatStartupErrorProps
> = ({ onRetry }) => {
  const { t, brand } = useI18nContext();
  const { displaySupportNumber, supportNumber } = brand.contactSupport;

  const actions: ChatStartupErrorContactAction[] = displaySupportNumber
    ? [
        {
          label: t("core-self-serve.contactSupport.callLabel"),
          phone: displaySupportNumber,
          tel: supportNumber ?? displaySupportNumber,
        },
      ]
    : [];

  return (
    <div className="hts-assist-chat-start-error">
      <ChatStartupError
        title={t("chat.htsAssistStartErrorTitle")}
        description={t("chat.htsAssistStartErrorMessage")}
        retryLabel={t("chat.htsAssistStartErrorRetry")}
        orLabel={t("chat.htsAssistStartErrorOr")}
        onRetry={onRetry}
        contact={
          actions.length > 0
            ? {
                title: t("core-self-serve.contactSupport.title"),
                description: t("chat.htsAssistStartErrorContactDescription"),
                actions,
                footnote: t("core-self-serve.contactSupport.callSubtitle"),
              }
            : undefined
        }
      />
    </div>
  );
};
